import React from "react"
import { Helmet } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { createBrowserRouter } from "react-router"
import { RouterProvider } from "react-router/dom"

import { AuthenticatedLayout } from "@/components/nav/authenticated-layout"
import {
  AuthenticationProvider,
  AuthorizationProvider,
  NotificationProvider,
} from "@/contexts"
import { RootPath } from "@/models/route"
import { adminRoutes } from "@/modules/admin"
import { dataRoutes } from "@/modules/data"
import { financialRoutes } from "@/modules/financialSummary"
import { insightsRoutes } from "@/modules/insights"
import { labsRoutes } from "@/modules/labs"
import { reportRoutes } from "@/modules/report"
import { settingsRoutes } from "@/modules/settings/routes"
import { siteRoutes } from "@/modules/site"
import { useAppId } from "@/services/app"
import { useGtag } from "@/services/gtag"
import { useConfigureValidationTranslation } from "@/services/i18n"
import { createNzeroosTheme, createRetrospecTheme } from "@/styles/themes"
import { getMuiLocaleCode } from "@/utils/i18n"
import { ConfirmProvider } from "material-ui-confirm"

import { CssBaseline } from "@mui/material"
import * as muiCoreLocales from "@mui/material/locale"
import { ThemeProvider } from "@mui/material/styles"
import * as muiDataGridLocales from "@mui/x-data-grid-pro/locales"
import * as muiDatePickersLocales from "@mui/x-date-pickers-pro/locales"

import { AppVersionUpdateNotification } from "../app-version-update-notification"
import { ForgotPassword } from "../forgot-password/forgot-password"
import { Login } from "../login"
import { Page404 } from "../nav/page404"
import { OrganizationIdRoute } from "../organization-id-route"
import { organizationIdLoader } from "../organization-id-route/organization-id-loader"
import { ResetPassword } from "../reset-password/reset-password"
import { AppLoader } from "../route/app-loader/app-loader"
import { AuthenticatedRoute } from "../route/authenticated-route/authenticated-route"
import { authenticatedRouteLoader } from "../route/authenticated-route/authenticated-route-loader"
import { rootIndexLoader } from "../route/root-index-loader"
import { RouteErrorElement } from "../route/route-error/route-error-element"
import { UnauthenticatedRoute } from "../route/unauthenticated-route/unauthenticated-route"
import { ValidateAccount } from "../validate-account/validate-account"

const routes = [
  // Unauthenticated routes
  {
    path: RootPath.SignIn,
    element: <UnauthenticatedRoute />,
    children: [
      {
        index: true,
        element: <Login />,
      },
    ],
  },
  {
    path: `${RootPath.ValidateAccount}/:token`,
    element: <UnauthenticatedRoute />,
    children: [
      {
        index: true,
        element: <ValidateAccount />,
      },
    ],
  },
  {
    path: RootPath.ForgotPassword,
    element: <UnauthenticatedRoute />,
    children: [
      {
        index: true,
        element: <ForgotPassword />,
      },
    ],
  },
  {
    path: `${RootPath.ResetPassword}/:token`,
    element: <UnauthenticatedRoute />,
    children: [
      {
        index: true,
        element: <ResetPassword />,
      },
    ],
  },
  // Authenticated routes
  {
    element: <AuthenticatedRoute />,
    errorElement: <RouteErrorElement />,
    loader: authenticatedRouteLoader,
    children: [
      {
        element: <AuthenticatedLayout />,
        errorElement: <RouteErrorElement />,
        children: [
          {
            index: true,
            loader: rootIndexLoader,
          },
          {
            path: ":orgId",
            element: <OrganizationIdRoute />,
            errorElement: <RouteErrorElement />,
            loader: organizationIdLoader,
            children: [
              ...adminRoutes,
              ...dataRoutes,
              ...financialRoutes,
              ...insightsRoutes,
              ...labsRoutes,
              ...reportRoutes,
              ...settingsRoutes,
              ...siteRoutes,
            ],
          },
          // Ensures authenticated route layout is rendered
          // when no matching route is found
          {
            path: "*",
            element: <Page404 />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Page404 />,
  },
]

type BrowserRouter = ReturnType<typeof createBrowserRouter>

const getBrowserRouter = (() => {
  let browserRouter: BrowserRouter | undefined

  return (): BrowserRouter =>
    (browserRouter ??= createBrowserRouter(
      routes as Parameters<typeof createBrowserRouter>[0]
    ))
})()

// AppLoader does not render children until boot finishes. Evaluating
// getBrowserRouter() as a RouterProvider prop still runs createBrowserRouter
// during splash and races loader fetchQuery with the same boot queries.
const AppRouter: React.FC = () => <RouterProvider router={getBrowserRouter()} />

export const App: React.FC = () => {
  const appId = useAppId()
  const { i18n } = useTranslation()
  useConfigureValidationTranslation()
  useGtag()

  // The active localized theme
  const activeTheme = React.useMemo(() => {
    const muiLocaleString = getMuiLocaleCode(i18n.language)

    const muiCoreLocale = (
      muiCoreLocales as Record<string, muiCoreLocales.Localization>
    )[muiLocaleString]

    const muiDatePickersLocale = (
      muiDatePickersLocales as Record<string, muiCoreLocales.Localization>
    )[muiLocaleString]

    const muiDataGridLocale = (
      muiDataGridLocales as Record<string, muiCoreLocales.Localization>
    )[muiLocaleString]

    return appId === "retrospec"
      ? createRetrospecTheme(
          muiCoreLocale,
          muiDataGridLocale,
          muiDatePickersLocale
        )
      : createNzeroosTheme(
          muiCoreLocale,
          muiDataGridLocale,
          muiDatePickersLocale
        )
  }, [appId, i18n.language])

  return (
    <>
      <Helmet>
        <html lang={i18n.resolvedLanguage} />
      </Helmet>
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <NotificationProvider>
          <ConfirmProvider>
            <AuthenticationProvider>
              <AuthorizationProvider>
                <AppLoader>
                  <AppRouter />
                  <AppVersionUpdateNotification />
                </AppLoader>
              </AuthorizationProvider>
            </AuthenticationProvider>
          </ConfirmProvider>
        </NotificationProvider>
      </ThemeProvider>
    </>
  )
}
